<template>
    <div v-if="totalPages > 1 || changed"
         class="Pagination"
    >
        <div class="Pagination--buttons">
            <div :class="['Pagination--buttons-btn', pageNumber < 1? 'controls-disabled': 'controls-active']"
                 @click="openPrevious">
                <i class="material-icons">keyboard_arrow_left</i>
            </div>
            <div v-for="(page, index) of pages"
                 :key="index"
                 :class="['Pagination--buttons-btn', {'active-btn': page === pageNumber + 1, 'cursor-pointer': Number.isInteger(page)}]"
                 @click="openPageItem(page - 1)">
                {{ page }}
            </div>
            <div :class="['Pagination--buttons-btn', pageNumber >= totalPages - 1? 'controls-disabled': 'controls-active']"
                 @click="openNext">
                <i class="material-icons">keyboard_arrow_right</i>
            </div>
        </div>

       <div class="d-flex flex-column">
           <div class="Pagination--control">
               <label>Show:</label>
               <select-box class="Pagination--control-el"
                           border="none"
                           height="40px"
                           border-radius="8px"
                           :data-list="pageSizeList"
                           :default-value="pageSize"
                           :required="false"
                           :is-default="false"
                           @selected="changeSize"
               />
               <label for="page">Go to page:</label>
               <div>
                   <input class="Pagination--control-el"
                          name="page"
                          ref="pageNumber"
                          id="page"
                          type="number"
                          v-validate="`numeric|min_value:1|max_value:${totalPages}`"
                          @input="changePageNumber"
                   />

               </div>
               <div>for {{ totalPages }}</div>
           </div>
       </div>

    </div>
</template>

<script>
    import { mapGetters, mapActions, mapMutations } from 'vuex';
    import * as types from '@/store/mutationTypes';

    export default {
        name: 'Pagination',

        components: {
            SelectBox: () => import('@/components/controls/SelectBox')
        },

        data() {
            return {
                pageSizeList : [5, 10, 15, 20, 25, 50],
                changed: false
            }
        },

        computed: {
            ...mapGetters([ 'totalElements', 'totalPages', 'pageSize', 'pageNumber' ]),

            pages() {
                let firstElement;
                const showPages = Math.min(5, this.totalPages);
                const currentNumber = this.pageNumber + 1;

                if (currentNumber < showPages) {
                    firstElement = 1;
                } else if (currentNumber >= this.totalPages - Math.ceil(showPages / 2)) {
                    firstElement = this.totalPages - showPages + 1;
                } else {
                    firstElement = currentNumber - Math.floor(showPages / 2);
                }

                return Array.from({length: showPages}, (x, i) => {
                    if (i === 0 && firstElement !== 1 && this.totalPages > showPages) {
                        return '...';
                    } else if (i === showPages - 1 && i + firstElement !== this.totalPages && this.totalPages > showPages) {
                        return '...';
                    }

                    return i + firstElement
                })
            }
        },

        methods: {
            ...mapActions([ 'openPreviousPage', 'openNextPage', 'openPage', 'changePageSize']),

            ...mapMutations({
                resetPagination: types.REMOVE_PAGINATION,
            }),

            clearPageNumberSelect() {
                this.$refs.pageNumber.value ='';
            },

            openPrevious() {
                this.openPreviousPage();
                this.clearPageNumberSelect();
            },

            openPageItem(page) {
                this.openPage(page);
                this.clearPageNumberSelect();
            },

            openNext() {
                this.openNextPage();
                this.clearPageNumberSelect();
            },

            changePageNumber(event) {
                setTimeout(() => {
                    if (!isNaN(Number(event.target.value))) {
                        this.openPage(event.target.value - 1);
                    }
                }, 300)
            },

            showPageIndex() {
                if (this.$refs.pageNumber && this.pageNumber !== null && !Number.isInteger(this.pageNumber)) {
                    this.$refs.pageNumber.value = this.pageNumber + 1;
                }
            },

            changeSize(size) {
                if (this.pageSize !== size) {
                    this.changed = true;
                }

                size && size !== this.pageSize && this.changePageSize(size);
            }
        },

        watch: {
            pageNumber() {
                this.showPageIndex();
            }
        },

        mounted() {
            this.showPageIndex();
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../assets/styles/variables/colors';

    .Pagination {
        width: 100%;
        display: flex;
        justify-content: space-between;
        margin-top: 30px;
        padding-right: 1.5rem;

        @media screen and (max-width: 1000px) {
            margin-top: 40px;
            position: initial;
            flex-direction: column;
            justify-content: initial;
        }

        &--buttons {
            display: flex;
            @media screen and (max-width: 1100px) {
                justify-content: center;
            }

            &-btn {
                width: 41px;
                height: 41px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-radius: 4px;
                margin-right: 1px;
                background-color: $White;
                font-size: 14px;
                cursor: pointer;

                &:last-child {
                    margin-right: 0;
                }

            }
            .active-btn {
                border-radius: 0;
                color: $White;
                box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.4);
                background-color: $LightBlue;
                cursor: no-drop;
            }

            .controls-disabled {
                cursor: no-drop;
                color: $ControlsGrey;
                pointer-events: none;
                cursor: not-allowed;
            }

            .controls-active {
                color: $Blue;
            }
        }

        &--control {
            display: flex;
            align-items: center;
            font-size: 14px;
            color: $Dark;

            @media screen and (max-width: 1100px) {
                display: none;
            }

            label {
                margin-bottom: 0;
                color: $Dark;
            }

            &-el {
                height: 40px;
                width: 80px !important;
                margin: 0 16px;
                border: 2px solid $ComplementaryBackgroundLightGrey;
                border-radius: 8px;

                &:focus {
                    border: 2px solid $Yellow;
                }
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/controls/Pagination.vue